import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BookOpen, HeartHandshake, Users, CalendarClock, Sparkles,
  ScrollText, Briefcase, Mic, Gamepad2, Lock, Eye, Zap, ListChecks, Database,
} from "lucide-react";
import { AppHeader } from "../components/AppHeader";
import { BottomSheet } from "../components/BottomSheet";
import { TimeCapsule } from "../components/TimeCapsule";
import { VaultChat } from "../components/VaultChat";
import { GameGenerator } from "../components/GameGenerator";
import { Kinworks } from "../components/Kinworks";
import { LegacyEstate } from "../components/LegacyEstate";
import { RespawnerStudio } from "../components/RespawnerStudio";
import { SecretVault } from "../components/SecretVault";
import { BuyCreditsSheet } from "../components/BuyCreditsSheet";
import { useAuth } from "../context/AuthContext";
import { trackVaultEnter, trackVaultExit } from "../lib/activity";

const AGENTS = {
  heritage_navigator: {
    greeting: "I'm your Heritage Navigator. Ask me how you're connected to anyone in the family and I'll trace the link, tell you who they are, and what you two have in common.",
    starters: ["How am I related to someone in the family?", "Who do I have the most in common with?", "Tell me about a relative I barely know"],
    placeholder: "Ask about a family connection…",
  },
  event_planner: {
    greeting: "I'm your Event Planner. Tell me who's coming and roughly when, and I'll design a plan tailored to your family's tastes.",
    starters: ["Plan a weekend hangout for everyone", "Ideas for our next family reunion", "Something fun for me and a couple of cousins"],
    placeholder: "What are we planning?",
  },
  historian: {
    greeting: "I'm The Historian. Ask me about the family's past — what happened, when it happened, and why.",
    starters: ["What's a defining moment in our family history?", "Tell me about our earliest ancestors", "What happened at past reunions?"],
    placeholder: "Ask about the family's past…",
  },
  mentor_matchmaker: {
    greeting: "I'm the Mentor Matchmaker. Answer a few quick questions and I'll find the best mentor or mentee for you in the family.",
    starters: ["Help me find a mentor", "Who could I mentor?", "Match me based on my interests"],
    placeholder: "Tell me what you're looking for…",
  },
};

const tools = [
  { key: "heritage_navigator", name: "Heritage Navigator", icon: HeartHandshake, desc: "Explore your roots" },
  { key: "event_planner", name: "Event Planner", icon: CalendarClock, desc: "Plan gatherings" },
  { key: "game_generator", name: "Game Generator", icon: Gamepad2, desc: "Family games & fun" },
  { key: "historian", name: "The Historian", icon: BookOpen, desc: "Family history archive" },
  { key: "kinworks", name: "KinWorks", icon: Briefcase, desc: "Family ventures" },
  { key: "mentor_matchmaker", name: "The Mentor Matchmaker", icon: Users, desc: "Pair mentors & mentees" },
  { key: "respawner", name: "The Respawner", icon: Sparkles, desc: "New beginnings" },
  { key: "legacy_suite", name: "Legacy Suite", icon: ScrollText, desc: "Wills & wishes" },
  { key: "time_capsule", name: "Time Capsule", icon: Mic, desc: "Record their stories" },
];

export default function Vault() {
  const { family, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openTool, setOpenTool] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [tcFeed, setTcFeed] = useState(false);
  const [svScheduled, setSvScheduled] = useState(false);
  const [svLibrary, setSvLibrary] = useState(false);
  const [svKey, setSvKey] = useState(0);

  const unlocked = family?.unlocked_vaults || tools.map((t) => t.key).concat("secret_vault");
  const isUnlocked = (key) => unlocked.includes(key);

  useEffect(() => {
    document.body.classList.add("lineage-lock");
    refreshUser && refreshUser();
    return () => document.body.classList.remove("lineage-lock");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (searchParams.get("tc")) {
      const tc = tools.find((t) => t.key === "time_capsule");
      if (tc && isUnlocked("time_capsule")) { setOpenTool(tc); setTcFeed(true); }
      setSearchParams({}, { replace: true });
    }
    if (searchParams.get("buy_credits")) {
      window.dispatchEvent(new Event("fv:open-credits"));
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (openTool) trackVaultEnter(openTool.key);
    else trackVaultExit();
    return () => trackVaultExit();
  }, [openTool]);

  useEffect(() => {
    const openSecret = () => {
      if (isUnlocked("secret_vault")) { setSvScheduled(false); setSvLibrary(false); setSvKey((k) => k + 1); setOpenTool({ key: "secret_vault", name: "The Secret Vault", icon: Lock, desc: "Private & secure" }); }
      else setShowUpgrade(true);
    };
    window.addEventListener("fv:open-secret-vault", openSecret);
    return () => window.removeEventListener("fv:open-secret-vault", openSecret);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked]);

  const secret = { key: "secret_vault", name: "The Secret Vault", icon: Lock, desc: "Private & secure" };
  const secretUnlocked = isUnlocked("secret_vault");

  const tap = (t) => { if (isUnlocked(t.key)) setOpenTool(t); else setShowUpgrade(true); };

  return (
    <div style={{ height: "100dvh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <AppHeader title="The Vault" />
      <div className="px-5 pt-2 flex flex-col flex-1 min-h-0" style={{ paddingBottom: 100 }}>
        <div className="flex items-center justify-between gap-3 mb-3 shrink-0 lg:mb-5">
          <p className="text-xs lg:text-sm" style={{ color: "var(--text-secondary)" }}>
            Ten powerful tools to help your family thrive across generations.
          </p>
          {user?.ai_credits && (
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("fv:open-credits"))}
              data-testid="vault-credits-pill"
              className="shrink-0 flex items-center gap-1.5 rounded-full pl-2.5 pr-3 py-1.5"
              style={{ background: "color-mix(in srgb, var(--accent) 12%, transparent)", border: "1px solid var(--accent)" }}>
              <Zap size={13} className="gold-text" />
              <span className="text-xs font-semibold gold-text" data-testid="vault-credits-count">
                {user.ai_credits.total_available.toLocaleString()}
              </span>
              <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>credits</span>
            </button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3 lg:gap-5 flex-1 min-h-0">
          {tools.map((t, i) => {
            const locked = !isUnlocked(t.key);
            return (
              <motion.button
                key={t.key}
                initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.04 }} whileTap={{ scale: 0.94 }}
                onClick={() => tap(t)} data-testid={`vault-tool-${i}`}
                className="relative rounded-2xl p-2 lg:p-5 flex flex-col items-center justify-center text-center card-surface overflow-hidden"
                style={{ boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }}>
                <div className={locked ? "flex flex-col items-center justify-center" : "flex flex-col items-center justify-center"}
                  style={locked ? { filter: "blur(3px)", opacity: 0.5 } : {}}>
                  <div className="h-9 w-9 lg:h-14 lg:w-14 rounded-xl grid place-items-center mb-1.5 lg:mb-3"
                    style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
                    <t.icon size={18} className="gold-text lg:hidden" />
                    <t.icon size={26} className="gold-text hidden lg:block" />
                  </div>
                  <span className="font-serif-display text-[12px] lg:text-xl leading-tight" style={{ color: "var(--text-primary)" }}>
                    {t.name.replace("The ", "")}
                  </span>
                  <span className="hidden lg:block text-xs mt-1.5 leading-snug" style={{ color: "var(--text-secondary)" }}>
                    {t.desc}
                  </span>
                </div>
                {locked && (
                  <div className="absolute inset-0 grid place-items-center px-1" data-testid={`vault-locked-${i}`}
                    style={{ background: "color-mix(in srgb, var(--bg-base) 45%, transparent)" }}>
                    <div className="text-center">
                      <Lock size={15} className="gold-text mx-auto mb-1" />
                      <span className="text-[9px] leading-tight block" style={{ color: "var(--text-primary)" }}>Want to unlock more vaults?</span>
                    </div>
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>

        <motion.button whileTap={{ scale: 0.98 }} onClick={() => tap(secret)} data-testid="vault-tool-9"
          className="mt-3 shrink-0 w-full card-surface rounded-2xl px-4 py-6 lg:py-4 text-left relative overflow-hidden">
          <div style={!secretUnlocked ? { filter: "blur(3px)", opacity: 0.5 } : {}}>
            <div className="flex items-center gap-2 gold-text mb-0.5">
              <Lock size={16} />
              <span className="text-xs uppercase tracking-widest">The Secret Vault</span>
            </div>
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
              A private, secure space for your family's most sensitive records and keepsakes.
            </p>
          </div>
          {!secretUnlocked && (
            <div className="absolute inset-0 grid place-items-center" data-testid="vault-locked-9"
              style={{ background: "color-mix(in srgb, var(--bg-base) 45%, transparent)" }}>
              <span className="text-[11px] flex items-center gap-1.5" style={{ color: "var(--text-primary)" }}>
                <Lock size={13} className="gold-text" /> Want to unlock more vaults?
              </span>
            </div>
          )}
        </motion.button>
      </div>

      <BottomSheet open={!!openTool} onClose={() => { setOpenTool(null); setTcFeed(false); setSvScheduled(false); setSvLibrary(false); }} title={openTool?.name || ""} height="90dvh"
        fill={!!AGENTS[openTool?.key] || openTool?.key === "legacy_suite" || openTool?.key === "respawner" || openTool?.key === "secret_vault"}
        leftAction={openTool?.key === "time_capsule" ? (
          <button onClick={() => setTcFeed((f) => !f)} data-testid="tc-feed-eye"
            className="h-9 w-9 rounded-full grid place-items-center" style={{ background: tcFeed ? "var(--accent)" : "var(--overlay)", border: "1px solid var(--border)" }}
            title={tcFeed ? "Back to Time Capsule" : "View Time Capsule feed"}>
            <Eye size={17} style={{ color: tcFeed ? "#fff" : "var(--text-secondary)" }} />
          </button>
        ) : openTool?.key === "secret_vault" ? (
          <div className="flex items-center gap-1.5">
            <button onClick={() => { setSvLibrary((f) => !f); setSvScheduled(false); }} data-testid="sv-library-eye"
              className="h-9 w-9 rounded-full grid place-items-center" style={{ background: svLibrary ? "var(--accent)" : "var(--overlay)", border: "1px solid var(--border)" }}
              title={svLibrary ? "Back to storing" : "View your document library"}>
              <Database size={16} style={{ color: svLibrary ? "#fff" : "var(--text-secondary)" }} />
            </button>
            <button onClick={() => { setSvScheduled((f) => !f); setSvLibrary(false); }} data-testid="sv-scheduled-eye"
              className="h-9 w-9 rounded-full grid place-items-center" style={{ background: svScheduled ? "var(--accent)" : "var(--overlay)", border: "1px solid var(--border)" }}
              title={svScheduled ? "Back to storing" : "View what you've scheduled"}>
              <ListChecks size={17} style={{ color: svScheduled ? "#fff" : "var(--text-secondary)" }} />
            </button>
          </div>
        ) : null}>
        {openTool?.key === "time_capsule" ? (
          <TimeCapsule feed={tcFeed} />
        ) : openTool?.key === "game_generator" ? (
          <GameGenerator />
        ) : openTool?.key === "kinworks" ? (
          <Kinworks />
        ) : openTool?.key === "legacy_suite" ? (
          <LegacyEstate />
        ) : openTool?.key === "respawner" ? (
          <RespawnerStudio />
        ) : openTool?.key === "secret_vault" ? (
          <SecretVault key={svKey} scheduledView={svScheduled} libraryView={svLibrary} />
        ) : AGENTS[openTool?.key] ? (
          <VaultChat
            tool={openTool.key}
            testid={`vault-chat-${openTool.key}`}
            greeting={AGENTS[openTool.key].greeting}
            starters={AGENTS[openTool.key].starters}
            placeholder={AGENTS[openTool.key].placeholder}
          />
        ) : (
          <div className="flex flex-col items-center text-center pt-6" data-testid="vault-tool-sheet">
            <div className="h-16 w-16 rounded-2xl grid place-items-center mb-4" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
              {openTool?.icon && <openTool.icon size={30} className="gold-text" />}
            </div>
            <div className="font-serif-display text-3xl">{openTool?.name}</div>
            <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>{openTool?.desc}</p>
            <div className="mt-8 rounded-2xl px-5 py-6 w-full" style={{ background: "var(--overlay)" }}>
              <Sparkles size={22} className="gold-text mx-auto" />
              <p className="text-sm mt-3" style={{ color: "var(--text-secondary)" }}>
                This experience is coming soon. We'll bring {openTool?.name} to life in the next iteration.
              </p>
            </div>
          </div>
        )}
      </BottomSheet>

      <BottomSheet open={showUpgrade} onClose={() => setShowUpgrade(false)} title="Unlock more vaults" height="70dvh">
        <div className="text-center pt-2" data-testid="vault-upgrade-sheet">
          <div className="h-14 w-14 rounded-2xl grid place-items-center mx-auto mb-3" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}>
            <Lock size={26} className="gold-text" />
          </div>
          <div className="font-serif-display text-2xl">Upgrade to unlock</div>
          <p className="text-sm mt-2 mb-5" style={{ color: "var(--text-secondary)" }}>
            Your current plan includes 3 vaults. Upgrade to open more of The Vault.
          </p>
          <div className="space-y-3 text-left">
            <div className="card-surface rounded-2xl p-4">
              <div className="font-serif-display text-xl">Branches</div>
              <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>Adds Historian, KinWorks & Mentor Matchmaker + unlimited posts & polls.</p>
            </div>
            <div className="card-surface rounded-2xl p-4">
              <div className="font-serif-display text-xl gold-text">Legacy</div>
              <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>Unlocks all 10 vaults, free reunions, and unlimited linked families.</p>
            </div>
          </div>
          <motion.button whileTap={{ scale: 0.97 }} onClick={() => { setShowUpgrade(false); navigate("/settings"); }}
            data-testid="vault-upgrade-cta"
            className="w-full mt-5 rounded-xl py-3.5 font-semibold text-sm" style={{ background: "var(--accent)", color: "#fff" }}>
            See upgrade options
          </motion.button>
        </div>
      </BottomSheet>

      <BuyCreditsSheet />
    </div>
  );
}
